import { useEffect, useRef, useState } from 'react';
import {
	getScreensaverSettings,
	subscribeScreensaverSettings,
} from '../../services/screensaverSettings';

/**
 * Events that count as "the user is still there". Deliberately not `scroll`:
 * a page that scrolls itself (a log tailing, a list settling after a fetch)
 * would hold the saver off forever.
 */
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart'];

/**
 * Start the screen saver after the idle delay set in Control Panel → Display.
 * A delay of 0 means the timer is off and the saver only runs on demand.
 *
 * `showing` is passed in so the timer stands down while the saver is up —
 * otherwise its own dismissal would immediately re-arm against a stale clock.
 */
export default function useScreensaverIdle(showing, onIdle) {
	const [delayMinutes, setDelayMinutes] = useState(
		() => getScreensaverSettings().delayMinutes,
	);
	const onIdleRef = useRef(onIdle);

	useEffect(() => { onIdleRef.current = onIdle; }, [onIdle]);

	useEffect(() => subscribeScreensaverSettings((next) => {
		setDelayMinutes(next.delayMinutes);
	}), []);

	useEffect(() => {
		if (showing || !delayMinutes) return undefined;
		if (typeof window === 'undefined') return undefined;

		const idleMs = delayMinutes * 60_000;
		let timer = window.setTimeout(function fire() {
			onIdleRef.current?.();
		}, idleMs);

		const bump = () => {
			window.clearTimeout(timer);
			timer = window.setTimeout(() => { onIdleRef.current?.(); }, idleMs);
		};

		// Capture phase, because a window that stops propagation on its own
		// pointer handling would otherwise look like an idle user.
		for (const type of ACTIVITY_EVENTS) {
			window.addEventListener(type, bump, { capture: true, passive: true });
		}
		return () => {
			window.clearTimeout(timer);
			for (const type of ACTIVITY_EVENTS) {
				window.removeEventListener(type, bump, { capture: true });
			}
		};
	}, [showing, delayMinutes]);

	return delayMinutes;
}
